import { T, useGT } from 'gt-react';
import { DownloadSimpleIcon, TrashIcon } from '@phosphor-icons/react';

import Page from 'src/components/page/Page';
import { Button } from '@/components/ui/button';
import {
  PRICE_LIST_BREAK_PIVOT_ACCEPT,
  validatePriceListBreakPivotFile,
} from '@/lib/price-list-break-pivot/excelFile';
import { FileDropzone } from '../../components/FileDropzone';
import { MappingControls } from './MappingControls';
import {
  CollisionsTable,
  FileResultList,
  PivotSummaryCards,
} from './PivotReadout';
import { usePriceListBreakPivot } from './usePriceListBreakPivot';

/**
 * Item Master → Utilities → Price List Break Pivot: collapse quantity-break
 * rows into one row per item with a column per break.
 */
export function PriceListBreakPivotPage() {
  const gt = useGT();
  const {
    views,
    headers,
    mapping,
    setMapping,
    summary,
    isParsing,
    addFiles,
    removeFile,
    clear,
    downloadPivot,
  } = usePriceListBreakPivot();

  return (
    <Page
      title={
        <T context="page title for the price list break pivot utility">
          Price List Break Pivot
        </T>
      }
      subtitle={
        <T context="subtitle for the price list break pivot utility">
          Drop a vendor workbook whose prices are split across quantity-break
          rows. Matching items collapse into one row with a column per break.
        </T>
      }
      actions={
        views.length > 0 ? (
          <div className="flex gap-2">
            {summary.pivotedSheetCount > 0 ? (
              <Button onClick={downloadPivot} disabled={isParsing}>
                <DownloadSimpleIcon className="h-4 w-4" />
                <T context="button that downloads the pivoted price list workbook">
                  Download Pivot
                </T>
              </Button>
            ) : null}
            <Button variant="outline" onClick={clear}>
              <TrashIcon className="h-4 w-4" />
              <T context="button that clears all dropped price list files">
                Clear Files
              </T>
            </Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col gap-4 pt-4">
        <FileDropzone
          multiple
          accept={PRICE_LIST_BREAK_PIVOT_ACCEPT}
          disabled={isParsing}
          prompt={
            <T context="instruction inside the price list break pivot drop area">
              Drag and drop .xls or .xlsx vendor price lists here.
            </T>
          }
          validate={(file) =>
            Promise.resolve(validatePriceListBreakPivotFile(file, gt))
          }
          onFilesAccepted={(files) => {
            void addFiles(files);
          }}
        />
        <MappingControls
          headers={headers}
          mapping={mapping}
          disabled={isParsing}
          onChange={setMapping}
        />
        <FileResultList
          views={views}
          onRemove={removeFile}
          isParsing={isParsing}
        />
        <PivotSummaryCards summary={summary} />
        <CollisionsTable collisions={summary.collisions} />
      </div>
    </Page>
  );
}
